import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBlockingMutation } from '../hooks/useBlockingMutation';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { Window, TextField, Button, Select } from '@warmancer/ui';
import type { AdminRole } from '@warmancer/shared';
import { authApi, ApiClientError } from '../api/client';
import { useAdminStore } from '../stores/adminStore';
import styles from './Login.module.css';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

const roleOptions = [
  { value: 'support', label: 'Support' },
  { value: 'mod', label: 'Moderator' },
  { value: 'super', label: 'Super Admin' },
];

export function Login() {
  const navigate = useNavigate();
  const setSession = useAdminStore((s) => s.setSession);

  const [role, setRole] = useState<AdminRole>('support');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);

  const loginMutation = useBlockingMutation({
    loadingMessage: 'Logging in...',
    mutationFn: () => authApi.login({ role, password, captcha_token: captchaToken || undefined }),
    onSuccess: (data) => {
      setSession(data.token, data.role);
      navigate('/');
    },
    onError: (err) => {
      // Reset captcha on error
      turnstileRef.current?.reset();
      setCaptchaToken(null);
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred');
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!password) {
      setError('Password is required');
      return;
    }

    if (!captchaToken) {
      setError('Please complete the CAPTCHA');
      return;
    }

    loginMutation.mutate();
  };

  return (
    <div className={styles.container}>
      <div className={styles.logo}>
        <h1 className={styles.title}>WARMANCER</h1>
        <p className={styles.subtitle}>Admin Panel</p>
      </div>

      <Window title="Admin Login" maxWidth={360} className={styles.window}>
        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <div className={styles.error}>{error}</div>}

          <Select
            name="role"
            label="Role"
            options={roleOptions}
            value={role}
            onChange={(e) => setRole(e.target.value as AdminRole)}
          />

          <TextField
            name="password"
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            autoFocus
          />

          <div className={styles.turnstile}>
            <Turnstile
              ref={turnstileRef}
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={setCaptchaToken}
              onError={() => setCaptchaToken(null)}
              onExpire={() => setCaptchaToken(null)}
            />
          </div>

          <Button
            type="submit"
            loading={loginMutation.isPending}
            disabled={loginMutation.isPending}
          >
            Login
          </Button>
        </form>
      </Window>
    </div>
  );
}
